import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, Undo2, AlertTriangle, Wallet, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { toast } from "sonner";

const fmt = (cents) => {
  if (cents == null) return "$0.00";
  return "$" + (Number(cents) / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ACCESS_LABEL = {
  general_event: "Evento general — sábado",
  dinner: "Cena especial — viernes",
  workshop: "Taller",
};

export const PaymentApprovalModal = ({ paymentId, kind = "individual", open, onClose, onUpdated }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [verifiedInput, setVerifiedInput] = useState("");
  const [amounts, setAmounts] = useState({});
  const [acting, setActing] = useState(false);
  const [previewEnt, setPreviewEnt] = useState(null);
  const [reverseAlloc, setReverseAlloc] = useState(null);
  const [reverseReason, setReverseReason] = useState("");

  const load = useCallback(async () => {
    if (!paymentId) return;
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(`/admin/payments/${paymentId}/allocation-summary`);
      setSummary(data);
      setVerifiedInput((data.verified_amount_cents / 100).toFixed(2));
    } catch (e) {
      setErr(apiError(e, "No se pudo cargar el pago."));
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const partial = useMemo(() => {
    if (!summary) return false;
    const anyActive = (summary.entitlements || []).some(e => e.status === "active");
    const anyPending = (summary.entitlements || []).some(e => e.status === "pending" && e.remaining_cents > 0);
    return anyActive && anyPending;
  }, [summary]);

  if (!open) return null;

  const saveVerified = async () => {
    setActing(true);
    try {
      const cents = Math.round(Number(verifiedInput) * 100);
      await api.put(`/admin/payments/${paymentId}/verified-amount`, { verified_amount_cents: cents });
      toast.success("Dinero verificado actualizado.");
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e, "No se pudo actualizar el dinero verificado."));
    } finally { setActing(false); }
  };

  const confirmAllocation = async (ent) => {
    const amount = Math.round(Number(amounts[ent.id] || 0) * 100);
    if (amount <= 0) { toast.error("Ingresa un monto mayor a cero."); return; }
    setActing(true);
    try {
      const { data } = await api.post(`/admin/payments/${paymentId}/allocations`, {
        entitlement_id: ent.id, amount_cents: amount, approve: true,
      });
      toast.success(data.entitlement_activated ? "Acceso aprobado." : "Abono registrado (acceso aún pendiente).");
      setAmounts({ ...amounts, [ent.id]: "" });
      setPreviewEnt(null);
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e, "No se pudo registrar el abono."));
    } finally { setActing(false); }
  };

  const confirmReverse = async () => {
    if ((reverseReason || "").trim().length < 5) { toast.error("Describe el motivo (≥5 caracteres)."); return; }
    setActing(true);
    try {
      await api.post(`/admin/payment-allocations/${reverseAlloc.id}/reverse`, { reason: reverseReason.trim() });
      toast.success("Asignación revertida.");
      setReverseAlloc(null); setReverseReason("");
      await load();
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e, "No se pudo revertir."));
    } finally { setActing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl" data-testid="payment-approval-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Aprobación por acceso
            {partial && <span className="rounded-full bg-hz-warning/15 px-2 py-0.5 text-xs font-bold text-hz-warning" data-testid="badge-partial">Parcialmente aprobado</span>}
          </DialogTitle>
        </DialogHeader>
        {loading && <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>}
        {err && <p className="rounded-lg border border-hz-error/40 bg-hz-error/5 px-3 py-2 text-xs text-hz-error" role="alert">{err}</p>}
        {!loading && summary && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-hz-gray/40 bg-hz-bg p-3 text-xs lg:grid-cols-4" data-testid="balances-grid">
              <div><p className="text-hz-ink/60">Dinero verificado</p><p className="font-bold text-hz-navy" data-testid="bal-verified">{fmt(summary.verified_amount_cents)}</p></div>
              <div><p className="text-hz-ink/60">Dinero asignado</p><p className="font-bold text-hz-navy" data-testid="bal-allocated">{fmt(summary.allocated_amount_cents)}</p></div>
              <div><p className="text-hz-ink/60">Disponible por asignar</p><p className="font-bold text-hz-success" data-testid="bal-unallocated">{fmt(summary.unallocated_amount_cents)}</p></div>
              <div><p className="text-hz-ink/60">Saldo pendiente</p><p className="font-bold text-hz-warning" data-testid="bal-outstanding">{fmt(summary.outstanding_amount_cents)}</p></div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold text-hz-ink">Verificar dinero del pago (MXN):</label>
              <Input type="number" step="0.01" min="0" value={verifiedInput} onChange={e => setVerifiedInput(e.target.value)} className="max-w-[160px]" data-testid="input-verified" />
              <Button size="sm" onClick={saveVerified} disabled={acting} data-testid="btn-save-verified">Guardar</Button>
            </div>
            <div>
              <p className="mb-2 text-xs font-bold text-hz-navy">Accesos y asignación</p>
              <div className="overflow-x-auto rounded-lg border border-hz-gray/40">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Acceso</TableHead>
                      <TableHead>Precio</TableHead>
                      <TableHead>Asignado</TableHead>
                      <TableHead>Pendiente</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Abonar (MXN)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary.entitlements || []).map(e => (
                      <TableRow key={e.id} data-testid={`ent-row-${e.id}`}>
                        <TableCell className="text-xs font-semibold">{ACCESS_LABEL[e.access_type] || e.access_type}</TableCell>
                        <TableCell className="text-xs">{fmt(e.unit_price_cents)}</TableCell>
                        <TableCell className="text-xs">{fmt(e.allocated_cents)}</TableCell>
                        <TableCell className="text-xs font-bold text-hz-warning">{fmt(e.remaining_cents)}</TableCell>
                        <TableCell className="text-xs">
                          {e.status === "active" ? <span className="rounded-full bg-hz-success/15 px-2 py-0.5 font-bold text-hz-success">Aprobado</span>
                            : e.status === "revoked" ? <span className="rounded-full bg-hz-error/15 px-2 py-0.5 font-bold text-hz-error">Revocado</span>
                            : <span className="rounded-full bg-hz-gray/30 px-2 py-0.5 font-bold text-hz-ink">Pendiente</span>}
                        </TableCell>
                        <TableCell>
                          {e.status !== "active" && e.remaining_cents > 0 && (
                            <div className="flex items-center gap-1">
                              <Input type="number" step="0.01" min="0" max={e.remaining_cents / 100}
                                value={amounts[e.id] || ""} onChange={ev => setAmounts({ ...amounts, [e.id]: ev.target.value })}
                                className="w-24" data-testid={`input-amount-${e.id}`} />
                              <Button size="sm" variant="outline" onClick={() => setPreviewEnt(e)} disabled={!amounts[e.id]} data-testid={`btn-preview-${e.id}`}>Vista previa</Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-bold text-hz-navy">Asignaciones activas</p>
              <ul className="divide-y divide-hz-gray/30 rounded-lg border border-hz-gray/40 bg-white text-xs" data-testid="alloc-list">
                {(summary.allocations || []).length === 0 && <li className="px-3 py-3 text-hz-ink/50">Sin asignaciones aún.</li>}
                {(summary.allocations || []).map(a => (
                  <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`alloc-row-${a.id}`}>
                    <div>
                      <p className="font-semibold text-hz-ink">{fmt(a.amount_cents)} <span className="text-hz-ink/50">→ {a.entitlement_id.slice(0, 8)}…</span></p>
                      <p className="text-[11px] text-hz-ink/50">{new Date(a.created_at).toLocaleString("es-MX")} · {a.notes || ""}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setReverseAlloc(a)} data-testid={`btn-reverse-${a.id}`}>
                      <Undo2 className="mr-1 h-3 w-3" /> Revertir
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="btn-close-approval">Cerrar</Button>
        </DialogFooter>

        {/* Preview antes de aplicar */}
        <Dialog open={!!previewEnt} onOpenChange={(o) => !o && setPreviewEnt(null)}>
          <DialogContent className="max-w-md" data-testid="allocation-preview-modal">
            <DialogHeader><DialogTitle>Vista previa</DialogTitle></DialogHeader>
            {previewEnt && (
              <div className="space-y-2 text-sm">
                <p>Se aplicará <strong>{fmt(Math.round(Number(amounts[previewEnt.id] || 0) * 100))}</strong> al acceso <strong>{ACCESS_LABEL[previewEnt.access_type]}</strong>.</p>
                <p className="text-xs text-hz-ink/70">Pendiente antes: {fmt(previewEnt.remaining_cents)}</p>
                <p className="text-xs text-hz-ink/70">Pendiente después: {fmt(Math.max(0, previewEnt.remaining_cents - Math.round(Number(amounts[previewEnt.id] || 0) * 100)))}</p>
                <p className="text-xs">{Math.round(Number(amounts[previewEnt.id] || 0) * 100) >= previewEnt.remaining_cents ? <span className="font-bold text-hz-success">El acceso será APROBADO.</span> : <span className="text-hz-warning">El acceso permanecerá pendiente (abono insuficiente).</span>}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewEnt(null)}>Cancelar</Button>
              <Button onClick={() => confirmAllocation(previewEnt)} disabled={acting} data-testid="btn-confirm-allocation" className="bg-hz-cyan text-white">
                {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Revertir con motivo */}
        <Dialog open={!!reverseAlloc} onOpenChange={(o) => !o && (setReverseAlloc(null) || setReverseReason(""))}>
          <DialogContent className="max-w-md" data-testid="reverse-allocation-modal">
            <DialogHeader><DialogTitle>Revertir asignación</DialogTitle></DialogHeader>
            <p className="text-xs text-hz-ink/60">Motivo (≥5 caracteres). Queda auditado.</p>
            <Input value={reverseReason} onChange={e => setReverseReason(e.target.value)} placeholder="Depósito duplicado, error de captura…" data-testid="input-reverse-reason" />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReverseAlloc(null); setReverseReason(""); }}>Cancelar</Button>
              <Button onClick={confirmReverse} disabled={acting} data-testid="btn-confirm-reverse" className="bg-hz-error text-white">
                {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />} Revertir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentApprovalModal;
